<template>
  <div class="navbar-wrap" ref="wrap">
    <div class="navbar" :class="{ 'navbar--sticky': isSticky }" :style="stickyStyle">
      <div class="navbar__inner">
        <nav class="navbar__nav">
          <a
            v-for="item in items"
            :key="item.key"
            :href="item.href || 'javascript:;'"
            class="navbar__link"
            :class="{ 'navbar__link--active': activeKey === item.key }"
            @click.prevent="handleClick(item)"
          >
            {{ item.label }}
            <span class="navbar__link-bar"></span>
          </a>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NavBar',
  props: {
    // 导航项配置：[{ key, label, target, href }]
    // target: 同页面锚点ID（如 '#section-intro'），href: 外部路由路径（如 '/awards'）
    items: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isSticky: false,
      activeKey: '',
      routeActiveKey: '',
      headerHeight: 60,
      navbarTop: 0,
      pageLoaded: false,
    }
  },
  computed: {
    stickyStyle() {
      if (!this.isSticky) return {}
      return { top: this.headerHeight + 'px' }
    },
  },
  watch: {
    activeKey(val) {
      if (!val) return
      this.$nextTick(() => this.scrollNavToActive())
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.measurePositions()
      this.initScrollSpy()
      this.bindSticky()
      // 根据当前路由匹配活跃导航项
      this.syncActiveFromRoute()
    })
    window.addEventListener('resize', this._onResize, { passive: true })
    // 页面已加载完成 → 直接启用吸顶；否则等 load 事件
    if (document.readyState === 'complete') {
      this.measurePositions()
      this.pageLoaded = true
      if (this._stickyHandler) this._stickyHandler()
    } else {
      const onLoad = () => {
        this.measurePositions()
        this.pageLoaded = true
        if (this._stickyHandler) this._stickyHandler()
      }
      window.addEventListener('load', onLoad)
      this._onLoad = onLoad
    }
  },
  beforeDestroy() {
    this.cleanup()
    window.removeEventListener('resize', this._onResize)
    if (this._onLoad) window.removeEventListener('load', this._onLoad)
  },
  methods: {
    handleClick(item) {
      this.activeKey = item.key
      this.$nextTick(() => this.scrollNavToActive())
      // 同时有 target 和 href ⇒ 跨页面跳转 + 滚动定位
      if (item.target && item.href) {
        const targetPath = item.href
        const currentPath = this.$route?.path || '/'
        const doScroll = () => {
          this.$nextTick(() => {
            setTimeout(() => this.scrollToTarget(item.target), 150)
          })
        }
        if (currentPath !== targetPath) {
          this.$router
            .push(targetPath)
            .then(doScroll)
            .catch(() => {})
        } else {
          doScroll()
        }
      } else if (item.target) {
        // 仅同页面锚点滚动
        this.scrollToTarget(item.target)
      } else if (item.href) {
        // 仅路由跳转
        this.$router.push(item.href).catch(() => {})
      }
    },
    scrollToTarget(target) {
      const el = document.querySelector(target)
      if (el) {
        const offset = (this.$el?.offsetHeight || 56) + 60
        const top = el.getBoundingClientRect().top + window.pageYOffset - offset
        this.smoothScrollTo(top, 600)
      }
    },
    smoothScrollTo(targetY, duration) {
      const startY = window.pageYOffset
      const diff = targetY - startY
      let startTime = null
      const step = (timestamp) => {
        if (!startTime) startTime = timestamp
        const progress = Math.min((timestamp - startTime) / duration, 1)
        // easeInOutCubic 缓动
        const ease = progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2
        window.scrollTo(0, startY + diff * ease)
        if (progress < 1) requestAnimationFrame(step)
      }
      requestAnimationFrame(step)
    },
    initScrollSpy() {
      const targets = this.items
        .filter((i) => i.target)
        .map((i) => ({ key: i.key, el: document.querySelector(i.target) }))
        .filter((t) => t.el)

      if (!targets.length) return

      const onScroll = () => {
        const offset = (this.$el?.offsetHeight || 56) + 80
        let active = ''
        for (const t of targets) {
          if (t.el.getBoundingClientRect().top <= offset) {
            active = t.key
          }
        }
        // 路由匹配的导航项优先保留（首页除外，由滚动侦测接管）
        this.activeKey = active || (this.routeActiveKey && this.$route?.path !== '/' ? this.routeActiveKey : '')
      }

      window.addEventListener('scroll', onScroll, { passive: true })
      onScroll()
      this._scrollHandler = onScroll
    },
    measurePositions() {
      const header = document.querySelector('.top-nvb_box')
      this.headerHeight = header ? header.offsetHeight : 60
      const wrap = this.$refs.wrap
      this.navbarTop = wrap ? wrap.offsetTop : 0
    },
    _onResize() {
      this.measurePositions()
    },
    bindSticky() {
      const onScroll = () => {
        // 页面未完全加载（图片未就绪）时不触发吸顶，避免 top 计算错误
        if (!this.pageLoaded) return
        this.isSticky = window.pageYOffset >= this.navbarTop
      }
      window.addEventListener('scroll', onScroll, { passive: true })
      onScroll()
      this._stickyHandler = onScroll
    },
    cleanup() {
      if (this._scrollHandler) window.removeEventListener('scroll', this._scrollHandler)
      if (this._stickyHandler) window.removeEventListener('scroll', this._stickyHandler)
    },
    // 手机端：当前激活项自动滚动到可视区域
    scrollNavToActive() {
      const nav = this.$el?.querySelector('.navbar__nav')
      if (!nav) return
      const activeLink = nav.querySelector('.navbar__link--active')
      if (!activeLink) return
      const navRect = nav.getBoundingClientRect()
      const linkRect = activeLink.getBoundingClientRect()
      // 如果激活项不在可视区域内，滚动导航条
      if (linkRect.left < navRect.left || linkRect.right > navRect.right) {
        activeLink.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
      }
    },
    // 根据当前路由匹配活跃导航项（用于非首页页面）
    syncActiveFromRoute() {
      const path = this.$route?.path || '/'
      const matched = this.items.find((i) => i.href === path)
      if (matched) {
        this.routeActiveKey = matched.key
        this.activeKey = matched.key
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// ==================== 导航条容器 ====================
.navbar-wrap {
  height: 50px;
  @media (min-width: 768px) {
    height: 56px;
  }
  @media (min-width: 996px) {
    height: 70px;
  }
}

// ==================== 主导航条 ====================
.navbar {
  width: 100%;
  background: #1a1a1a;
  z-index: 900;
  transition: box-shadow 0.3s;

  &--sticky {
    position: fixed;
    left: 0;
    width: 100%;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
  }

  &__inner {
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    @media (min-width: 768px) {
      height: 56px;
    }
    @media (min-width: 996px) {
      height: 70px;
    }
  }
}

// ==================== 导航链接（PC + 手机通用） ====================
.navbar__nav {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  padding: 0 8px;
  // 手机端横向滚动
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  @media (min-width: 768px) {
    gap: 12px;
    padding: 0 16px;
    overflow-x: auto;
  }
  @media (min-width: 996px) {
    justify-content: center;
    overflow-x: visible;
  }
}

.navbar__link {
  position: relative;
  display: inline-block;
  flex-shrink: 0;
  padding: 6px 12px;
  font-size: 15px;
  font-weight: 500;
  color: #bbb;
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.25s;

  @media (min-width: 568px) {
    font-size: 16px;
  }
  @media (min-width: 768px) {
    padding: 6px 20px;
    font-size: 20px;
  }
  @media (min-width: 996px) {
    padding: 10px 24px;
    font-size: 26px;
  }

  &:hover {
    color: #fff;
  }

  &--active {
    color: #f48e30 !important;
    font-weight: 600;
  }

  &-bar {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%) scaleX(0);
    width: 60%;
    height: 2px;
    background: #f48e30;
    border-radius: 1px;
    transition: transform 0.25s ease;
  }

  &:hover &-bar,
  &--active &-bar {
    transform: translateX(-50%) scaleX(1);
  }
}
</style>
